import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, doc, onSnapshot } from 'firebase/firestore';

import * as authService from '../../services/auth';
import api from '../../services/axiosApi';
import { firestoreDb } from '../../services/firebase';
import { handleErrorNotification } from '../actions/notifications';
import { getUserDepots } from '../selectors/user';
import { addEnvironmentMode } from '../../models/depot';
import { PERMISSION_DENIED } from '../../constants/strings';
import { getErrorMessage } from '../../utils/errors';

const initialState = {
  uid: '',
  email: '',
  error: null,
  photoURL: '',
  metadata: {},
  loading: false,
};

export const login = createAsyncThunk(
  'routePlannerUser/login',
  async (_, { rejectWithValue }) => {
    try {
      const loggedIn = await authService.login();
      if (!loggedIn) {
        return rejectWithValue(PERMISSION_DENIED);
      }
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const logout = createAsyncThunk(
  'routePlannerUser/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await authService.logout();
      dispatch(setUserData(null));
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const configureUserDepot = createAsyncThunk(
  'routePlannerUser/configureUserDepot',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const userDepots = getUserDepots(getState());
    try {
      const depotDoc = doc(collection(firestoreDb, 'config'), 'depot');
      onSnapshot(depotDoc, (snapshot) => {
        if (snapshot.exists() && userDepots?.length) {
          const depotEnvSettings = snapshot.data();
          dispatch(
            updateUserDepotData(
              addEnvironmentMode(depotEnvSettings, userDepots)
            )
          );
        }
      });
    } catch (error) {
      dispatch(handleErrorNotification(error));
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchUserMetadata = createAsyncThunk(
  'routePlannerUser/fetchUserMetadata',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { user },
      } = await api.get(`/user`);
      dispatch(setUserMetaData(user));
      dispatch(configureUserDepot());
      return user;
    } catch (error) {
      dispatch(handleErrorNotification(error));
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const onUserAuth = createAsyncThunk(
  'routePlannerUser/onUserAuth',
  async (user, { dispatch, rejectWithValue }) => {
    try {
      if (user) {
        dispatch(
          setUserData({
            email: user.email,
            name: user.displayName,
            uid: user.id,
            photoURL: user.photoURL,
          })
        );
        await dispatch(fetchUserMetadata(user.id));
        return user;
      }
      return null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const routePlannerUserSlice = createSlice({
  name: 'routePlannerUser',
  initialState,
  reducers: {
    setUserData(state, action) {
      return action.payload ? { ...action.payload } : initialState;
    },
    setUserMetaData(state, action) {
      state.metadata = action.payload;
    },
    updateUserDepotData(state, action) {
      state.metadata = {
        ...state.metadata,
        depotAccess: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserMetadata.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserMetadata.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchUserMetadata.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(configureUserDepot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(configureUserDepot.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(configureUserDepot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(onUserAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(onUserAuth.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(onUserAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUserData, setUserMetaData, updateUserDepotData } =
  routePlannerUserSlice.actions;

export default routePlannerUserSlice.reducer;
